import {
  readBoPreference,
  persistBoPreference,
} from "@/lib/user-preferences/store";
import { VisibilityState } from "@tanstack/react-table";
import { useCallback, useState } from "react";

export type TablePreferencesPayload = {
  order: string[];
  columnOrder: string[];
  visibility: Record<string, boolean>;
};

export function readTablePreferences(
  storageKey: string,
): Partial<TablePreferencesPayload> | null {
  const saved = readBoPreference<TablePreferencesPayload>(storageKey);
  if (!saved || typeof saved !== "object") return null;
  return saved;
}

export function useTablePreferences(
  storageKey: string,
  defaultOrder: string[],
  defaultVisibility: VisibilityState,
) {
  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = readTablePreferences(storageKey);
    return saved?.order ?? saved?.columnOrder ?? defaultOrder;
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = readTablePreferences(storageKey);
      return (saved?.visibility as VisibilityState) ?? defaultVisibility;
    },
  );

  const persistTableState = useCallback(
    (order: string[], visibility: VisibilityState) => {
      persistBoPreference(storageKey, {
        order,
        columnOrder: order,
        visibility: visibility as Record<string, boolean>,
      });
    },
    [storageKey],
  );

  const setColumnsOrderAndPersist = useCallback(
    (order: string[] | ((prev: string[]) => string[])) => {
      setColumnsOrder((prev) => {
        const next = typeof order === "function" ? order(prev) : order;
        persistTableState(next, columnVisibility);
        return next;
      });
    },
    [columnVisibility, persistTableState],
  );

  const setColumnVisibilityAndPersist = useCallback(
    (
      visibility:
        | VisibilityState
        | ((prev: VisibilityState) => VisibilityState),
    ) => {
      setColumnVisibility((prev) => {
        const next = typeof visibility === "function" ? visibility(prev) : visibility;
        persistTableState(columnsOrder, next);
        return next;
      });
    },
    [columnsOrder, persistTableState],
  );

  return {
    columnsOrder,
    setColumnsOrder: setColumnsOrderAndPersist,
    columnVisibility,
    setColumnVisibility: setColumnVisibilityAndPersist,
  };
}
